import clsx from 'clsx'
import { GetServerSideProps } from 'next'
import Head from 'next/head'
import React, { useContext, useEffect } from 'react'

import { CheckoutEmptyCart } from '@Domains/Checkout/EmptyCart'
import { AdyenContextProviderV2, AdyenV2ConfigProps } from '@Domains/Checkout/Providers/AdyenContextProvider'
import { BillwerkContextProvider } from '@Domains/Checkout/Providers/BillwerkContextProvider'
import { BraintreeConfig, BrainTreeContextProviderV2 } from '@Domains/Checkout/Providers/BraintreeContextProvider'
import { CheckoutModalProvider } from '@Domains/Checkout/Providers/CheckoutModalProvider'
import RecaptchaContextProvider from '@Domains/Checkout/Providers/RecaptchaContextProvider'
import { CheckoutFlow } from '@Domains/CheckoutV2/Features/CheckoutSteps/CheckoutFlow'
import { CheckoutFlowLogo } from '@Domains/CheckoutV2/Features/CheckoutSteps/Components/CheckoutFlowLogo/CheckoutFlowLogo'
import { CheckoutFlowProvider } from '@Domains/CheckoutV2/Features/CheckoutSteps/Contexts/CheckoutFlowContext'
import { FooterTrustpilotAndDelivery } from '@Domains/Delivery/Features/FooterTrustpilotAndDelivery/FooterTrustpilotAndDelivery'
import { CheckoutCartQuery, FinalizeCartMutation, Scalars, useCheckoutCartQuery } from '@Generated/graphql'
import { initializeApolloFromContext } from '@Globals/Apollo'
import { generateEventID } from '@Globals/Google/DataLayer'
import { lineItemsToGA4Items, toMultiCurrency } from '@Globals/Google/ga4Helpers'
import { gtagEvent } from '@Globals/Google/GTag'
import { MetadataContext } from '@Globals/Metadata'
import { PageHOC } from '@Globals/Page/Client'
import { pageProps } from '@Globals/Page/Server'
import { PageProps } from '@Globals/Page/Types'
import { useSplitTest } from '@Globals/SplitTests/Hooks'
import { useTranslation } from '@Globals/Translations'
import { getCheckout } from '@Queries/cart/Checkout.query'
import { getZipCodeForCountryCode } from '@Shared/Utilities/ZipCodeHelpers/ZipCodeHelper'

import styles from './oneStepCheckout.module.scss'

export type CheckoutV2Type = NonNullable<CheckoutCartQuery['checkout']>
export type CheckoutCartV2 = NonNullable<CheckoutV2Type['cart']>
export type CheckoutCartV2BillingAddress = Omit<NonNullable<CheckoutCartV2['billingAddress']>, '__typename'>
export type CheckoutCartV2ShippingAddress = Omit<NonNullable<CheckoutCartV2['shippingAddress']>, '__typename'>
export type CheckoutCartV2Total = Omit<NonNullable<CheckoutCartV2['totals']>, '__typename'>[0]
export type CheckoutCartV2ShippingMethod = NonNullable<CheckoutCartV2['availableShippingMethods']>[0]
export type CheckoutCartV2Address = CheckoutCartV2BillingAddress | CheckoutCartV2ShippingAddress
export type CheckoutV2MetaData = NonNullable<CheckoutCartQuery['checkout']>
export type CheckoutV2AddressMeta = NonNullable<CheckoutV2MetaData['addressMetaData']>
export type CheckoutV2ServiceLocation = NonNullable<CheckoutCartV2ShippingMethod['serviceLocations']>[0]
export type CheckoutV2PaymentMethod = NonNullable<CheckoutCartV2['availablePaymentMethods']>[0]
export type CheckoutV2FinalizeCartResult = NonNullable<FinalizeCartMutation['finalizeCart']> & {
  error: {
    code: Scalars['String']
    message: Scalars['String']
  } | string
  errors: Array<{
    code: Scalars['String']
    message: Scalars['String']
  } | string>
}

interface CheckoutPageProps extends PageProps {
  zipCodes?: Record<string, string>
}

export const getServerSideProps: GetServerSideProps<CheckoutPageProps> = async (context) => {
  const apolloClient = initializeApolloFromContext(context)
  // Pass the cookie to the checkout endpoint
  const headers: HeadersInit = {}
  const { cookie } = context.req.headers
  if (cookie) {
    headers.cookie = cookie
  }
  const zipCodes = await getZipCodeForCountryCode(context.req.thStore.config?.countryCode)
  const res = await getCheckout(apolloClient)
  const isCodAvailable = !!(res.data.checkout?.cart?.availableShippingMethods || []).find(asm => asm.isCOD)
  const isDroppointAvailable = !!(res.data.checkout?.cart?.availableShippingMethods || []).find(asm => asm.isServiceLocation)

  return {
    props: {
      zipCodes,
      ...await pageProps(
        context,
        {
          type: 'cof',
          id: '',
          attributes: {
            is_cod_available: isCodAvailable,
            is_droppoint_available: isDroppointAvailable,
          },
        },
        {
          url: '/onestepcheckout',
        },
        ['checkout', 'cart', 'common', 'header', 'shipping', 'cms', 'products', 'validation'],
        true,
      ),
      initialApolloState: apolloClient.cache.extract(),
    },
  }
}

const CheckoutPage = PageHOC<CheckoutPageProps>(({ zipCodes }) => {
  const cofDesignV1 = useSplitTest('cof_design_v1', 'b')
  const { t } = useTranslation()
  const {
    data,
    loading,
    refetch,
  } = useCheckoutCartQuery({
    context: {
      domainProxy: true,
      userSpecific: true,
    },
  })
  const cart = data?.checkout?.cart
  const checkoutMeta = data?.checkout || null
  const isCartEmpty = !cart || !cart.lineItems || cart.lineItems.length === 0
  const { store } = useContext(MetadataContext)
  useEffect(() => {
    if (!cart?.lineItems?.length) {
      return
    }
    const currencyCode = store.config?.currencyCode
    const rates = store.config?.adwordsCurrencyRates
    const value = parseFloat(cart.totals?.find(total => total.id === 'grand_total')?.value?.plain || '0') || 0
    gtagEvent('begin_checkout', {
      event_id: generateEventID(),
      currency: currencyCode,
      value,
      ...toMultiCurrency(value, currencyCode, rates),
      items: lineItemsToGA4Items(cart.lineItems, currencyCode, rates),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart?.id])
  if (!checkoutMeta) {
    return null
  }

  return (
    <>
      <Head>
        <meta content="width=device-width, initial-scale=1, maximum-scale=1.0, user-scalable=no" name="viewport" />
        <title>{t('checkout:CHECKOUT')}</title>
      </Head>
      <RecaptchaContextProvider>
        <div className={clsx(styles.checkoutLayout, cofDesignV1 && styles.cofDesignV1)}>
          <CheckoutFlowLogo />
          <div className={styles.content}>
            {isCartEmpty && <CheckoutEmptyCart />}
            {!isCartEmpty && (
              <CheckoutModalProvider onClosedModal={() => {
                refetch()
              }}
              >
                <CheckoutFlowProvider
                  data={{
                    cart,
                    checkoutMeta,
                  }}
                  loading={loading}
                  zipCodes={zipCodes || {}}
                >
                  <BillwerkContextProvider>
                    <BrainTreeContextProviderV2
                      braintreeConfig={(checkoutMeta.paymentMetaData?.braintreeConfig
                        ? (checkoutMeta.paymentMetaData.braintreeConfig as unknown as BraintreeConfig)
                        : undefined)}
                      paymentOptions={cart.availablePaymentMethods || []}
                    >
                      <AdyenContextProviderV2
                        adyenConfig={checkoutMeta.paymentMetaData?.adyenConfig as (AdyenV2ConfigProps | null)}
                      >
                        <CheckoutFlow />
                      </AdyenContextProviderV2>
                    </BrainTreeContextProviderV2>
                  </BillwerkContextProvider>
                </CheckoutFlowProvider>
              </CheckoutModalProvider>
            )}
          </div>
          <FooterTrustpilotAndDelivery />
        </div>
      </RecaptchaContextProvider>
    </>
  )
})

export default CheckoutPage
